import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg, fmtTime } from "@/lib/api";
import { card, Field, Sel, inputCls, PatientSelect, Empty } from "@/components/admin/kit";
import { CallTypePicker, callBody } from "@/components/admin/CallTypePicker";

const inMinutes = (n) => {
  const d = new Date(Date.now() + n * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function TestNumbers({ numbers, onSaved }) {
  const api = useApi();
  const [n, setN] = useState("");
  const save = async (list) => {
    try { await api.put("/settings/test-numbers", { numbers: list }); setN(""); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="test-numbers">
      <p className="text-sm font-semibold">Verified test numbers</p>
      <p className="text-xs text-muted-foreground">Scheduled calls only go to these numbers. Use phones you own (verified in Twilio).</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {numbers.map((x) => <span key={x} className="flex items-center gap-1 rounded-full bg-[var(--lav)] px-3 py-1 text-xs font-semibold" data-testid={`test-number-${x}`}>{x}<button onClick={() => save(numbers.filter((y) => y !== x))} data-testid={`test-number-remove-${x}`}><X size={12} /></button></span>)}
      </div>
      <div className="mt-2 flex gap-2"><input className={inputCls} placeholder="+91 98xxxxxxxx" value={n} onChange={(e) => setN(e.target.value)} data-testid="test-number-input" /><Button variant="outline" disabled={!n} onClick={() => save([...numbers, n])} data-testid="test-number-add-btn">Add</Button></div>
    </div>
  );
}

const STATUS = { scheduled: "bg-sky-100 text-sky-800", placing: "bg-amber-100 text-amber-800", placed: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-700", missed: "bg-red-100 text-red-700", cancelled: "bg-gray-100 text-gray-600" };

export function ScheduleTestCall({ prov }) {
  const api = useApi();
  const [settings, reloadS] = useLoad("/settings");
  const [list, reload] = useLoad("/calls/scheduled");
  const [q, setQ] = useState({ patient_id: "", kind: "daily", slot: "morning", medicine_id: "", appointment_id: "", to: "", at: inMinutes(2) });
  const nums = settings?.test_numbers || [];
  const masked = nums.map((n) => [n, n.length === 13 && n.startsWith("+91") ? `+91 ${n.slice(3, 8)} ••${n.slice(-3)}` : n]);
  const waiting = (list || []).some((x) => ["scheduled", "placing"].includes(x.status));
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [waiting, reload]);
  const schedule = async () => {
    try { await api.post("/calls/schedule", { ...callBody(q), to: q.to, at: new Date(q.at).toISOString() }); toast.success("Scheduled — the call will arrive by itself at that time"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`${card} mt-4 grid gap-4`} data-testid="schedule-test-call">
      <div><p className="font-bold">Schedule test call</p><p className="text-xs text-muted-foreground">Places ONE real call at the time you pick, to a verified test number. Max 5 a day. Keep this tab or not — the server places it.</p></div>
      <TestNumbers numbers={nums} onSaved={reloadS} />
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Patient (needs consent)"><PatientSelect value={q.patient_id} onChange={(v) => setQ({ ...q, patient_id: v })} testid="schedule-patient-select" /></Field>
        <CallTypePicker q={q} setQ={setQ} idp="schedule" />
        <Field label="Call this number"><Sel value={q.to} onChange={(v) => setQ({ ...q, to: v })} placeholder="Choose" options={masked} testid="schedule-to-select" /></Field>
        <Field label="At (your time)"><input type="datetime-local" className={inputCls} value={q.at} onChange={(e) => setQ({ ...q, at: e.target.value })} data-testid="schedule-at-input" /></Field>
        <div className="flex items-end gap-2"><Button variant="outline" onClick={() => setQ({ ...q, at: inMinutes(2) })} data-testid="schedule-in-2-btn">In 2 min</Button>
          <Button className="flex-1" disabled={!q.patient_id || !q.to || prov === "simulator"} onClick={schedule} data-testid="schedule-call-btn"><CalendarClock size={16} /> Schedule test call</Button></div>
      </div>
      {!(list || []).length ? <Empty>No scheduled test calls yet.</Empty> : (
        <div className="grid gap-2" data-testid="scheduled-list">
          {list.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center gap-2 rounded-2xl bg-[var(--lav)] px-3 py-2 text-sm" data-testid={`scheduled-${x.id}`}>
              <b>{fmtTime(x.at)}</b><span>{x.patient_name} · {x.kind}{x.slot ? ` · ${x.slot}` : ""} · {x.to_masked}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS[x.status]}`} data-testid={`scheduled-status-${x.id}`}>{x.status}</span>
              {x.error && <span className="text-xs text-red-700">{x.error}</span>}
              {x.status === "scheduled" && <Button size="sm" variant="ghost" className="ml-auto" onClick={async () => { await api.post(`/calls/scheduled/${x.id}/cancel`); reload(); }} data-testid={`scheduled-cancel-${x.id}`}>Cancel</Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
